// Construction des rapports de l'espace Propriétaire (DG).
// Toutes les lignes proviennent des données accessibles au compte connecté :
// immeubles, biens, locataires, contrats, loyers, paiements, dépenses,
// maintenance et documents. Aucune donnée fictive n'est ajoutée.
// Le jour où la base de données est branchée, seul le contexte change.

import type {
  ReportFilters,
  ReportResult,
  ReportRow,
  ReportTotal,
  StatutFilterConfig,
} from "@/components/feature/reports/types";
import { inDateRange, inPeriodRange, periodKeyOf } from "@/components/feature/reports/utils";
import { formatDate, formatFcfa } from "@/utils/format";
import type { Bien } from "@/pages/proprietaire/biens/types";
import { BIEN_STATUT_LABELS, BIEN_TYPE_LABELS } from "@/pages/proprietaire/biens/types";
import type { Contrat } from "@/pages/proprietaire/contrats/types";
import { CONTRAT_STATUT_LABELS, contratReference } from "@/pages/proprietaire/contrats/types";
import type { Depense } from "@/pages/proprietaire/depenses/types";
import { DEPENSE_CATEGORIE_LABELS } from "@/pages/proprietaire/depenses/types";
import type { DocumentItem } from "@/pages/proprietaire/documents/types";
import { DOCUMENT_TYPE_LABELS, formatFileSize } from "@/pages/proprietaire/documents/types";
import type { Immeuble } from "@/pages/proprietaire/immeubles/types";
import type { Intervention } from "@/pages/proprietaire/maintenance/types";
import {
  INTERVENTION_PRIORITE_LABELS,
  INTERVENTION_STATUT_LABELS,
} from "@/pages/proprietaire/maintenance/types";
import { monthLabel } from "@/pages/proprietaire/loyers/periods";
import type { Loyer } from "@/pages/proprietaire/loyers/types";
import { LOYER_STATUT_LABELS, isImpaye } from "@/pages/proprietaire/loyers/types";
import type { Locataire } from "@/pages/proprietaire/locataires/types";
import { locataireNomComplet, TYPE_PIECE_LABELS } from "@/pages/proprietaire/locataires/types";
import type { Paiement } from "@/pages/proprietaire/paiements/types";
import { MODE_PAIEMENT_LABELS, paiementReference } from "@/pages/proprietaire/paiements/types";

export interface OwnerReportContext {
  immeubles: Immeuble[];
  biens: Bien[];
  locataires: Locataire[];
  contrats: Contrat[];
  loyers: Loyer[];
  paiements: Paiement[];
  depenses: Depense[];
  interventions: Intervention[];
  documents: DocumentItem[];
}

const OPTION = (value: string, label: string) => ({ value, label });

/** Filtre contextuel (statut / type / catégorie) propre à chaque rapport. */
export function getOwnerStatutConfig(typeId: string): StatutFilterConfig {
  switch (typeId) {
    case "biens":
      return { label: "Statut", options: Object.entries(BIEN_STATUT_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "contrats":
      return { label: "Statut", options: Object.entries(CONTRAT_STATUT_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "loyers":
      return { label: "Statut", options: Object.entries(LOYER_STATUT_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "impayes":
      return { label: "Statut", options: [OPTION("en-retard", "En retard"), OPTION("impaye", "Impayé")] };
    case "depenses":
      return { label: "Catégorie", options: Object.entries(DEPENSE_CATEGORIE_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "maintenance":
      return { label: "Statut", options: Object.entries(INTERVENTION_STATUT_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "documents":
      return { label: "Type de document", options: Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => OPTION(value, label)) };
    default:
      return { label: "Statut", options: [] };
  }
}

/* ---------------------------------------------------------------- */
/* Aides de résolution et de filtrage                                */
/* ---------------------------------------------------------------- */

const immeubleNom = (ctx: OwnerReportContext, id: string) =>
  ctx.immeubles.find((item) => item.id === id)?.nom ?? "—";

const bienLabel = (ctx: OwnerReportContext, id: string) => {
  const bien = ctx.biens.find((item) => item.id === id);
  return bien ? `${BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` ${bien.reference}` : ""}` : "—";
};

const locataireNom = (ctx: OwnerReportContext, id: string) => {
  const locataire = ctx.locataires.find((item) => item.id === id);
  return locataire ? locataireNomComplet(locataire) : "—";
};

const total = (label: string, value: string, tone: ReportTotal["tone"] = "neutral"): ReportTotal => ({ label, value, tone });

const matchImmeuble = (immeubleId: string, f: ReportFilters) => !f.immeubleId || immeubleId === f.immeubleId;
const matchBien = (bienId: string, f: ReportFilters) => !f.bienId || bienId === f.bienId;
const matchLocataire = (locataireId: string, f: ReportFilters) => !f.locataireId || locataireId === f.locataireId;

const sumFcfa = (values: number[]) => formatFcfa(values.reduce((sum, value) => sum + value, 0));

/* ---------------------------------------------------------------- */
/* Rapports                                                          */
/* ---------------------------------------------------------------- */

function reportImmeubles(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.immeubles
    .filter((immeuble) => matchImmeuble(immeuble.id, f))
    .filter((immeuble) => inDateRange(immeuble.createdAt, f.dateFrom, f.dateTo));
  const biensCount = (id: string) => ctx.biens.filter((bien) => bien.immeubleId === id).length;
  const locatairesCount = (id: string) =>
    new Set(ctx.contrats.filter((contrat) => contrat.immeubleId === id).map((contrat) => contrat.locataireId)).size;

  const rows = filtered.map<ReportRow>((immeuble) => ({
    nom: immeuble.nom,
    localisation: [immeuble.region, immeuble.commune].filter(Boolean).join(" · ") || "—",
    adresse: immeuble.adresse || "—",
    niveaux: immeuble.niveaux ?? 0,
    biens: biensCount(immeuble.id),
    locataires: locatairesCount(immeuble.id),
  }));

  return {
    title: "Rapport des immeubles",
    description: "Vue détaillée de votre patrimoine immobilier.",
    columns: [
      { key: "nom", header: "Immeuble" },
      { key: "localisation", header: "Localisation" },
      { key: "adresse", header: "Adresse" },
      { key: "niveaux", header: "Niveaux", align: "right" },
      { key: "biens", header: "Biens", align: "right" },
      { key: "locataires", header: "Locataires", align: "right" },
    ],
    rows,
    totals: [
      total("Immeubles", String(filtered.length), "primary"),
      total("Biens rattachés", String(filtered.reduce((sum, immeuble) => sum + biensCount(immeuble.id), 0)), "secondary"),
      total("Locataires rattachés", String(filtered.reduce((sum, immeuble) => sum + locatairesCount(immeuble.id), 0)), "accent"),
    ],
  };
}

function reportBiens(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.biens
    .filter((bien) => matchImmeuble(bien.immeubleId, f) && matchBien(bien.id, f))
    .filter((bien) => !f.statut || bien.statut === f.statut)
    .filter((bien) => inDateRange(bien.createdAt, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((bien) => ({
    type: BIEN_TYPE_LABELS[bien.type],
    reference: bien.reference || "—",
    immeuble: immeubleNom(ctx, bien.immeubleId),
    emplacement: [bien.etage, bien.numero].filter(Boolean).join(" · ") || "—",
    surface: bien.surface ? `${bien.surface} m²` : "—",
    loyer: formatFcfa(bien.loyerMensuel),
    statut: BIEN_STATUT_LABELS[bien.statut],
  }));

  return {
    title: "Rapport des biens",
    description: "Inventaire des biens avec loyer et statut d'occupation.",
    columns: [
      { key: "type", header: "Type" },
      { key: "reference", header: "Référence" },
      { key: "immeuble", header: "Immeuble" },
      { key: "emplacement", header: "Emplacement" },
      { key: "surface", header: "Surface", align: "right" },
      { key: "loyer", header: "Loyer mensuel", align: "right" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Biens", String(filtered.length), "primary"),
      total("Loyer mensuel cumulé", sumFcfa(filtered.map((bien) => bien.loyerMensuel)), "secondary"),
      total("Occupés", String(filtered.filter((bien) => bien.statut === "occupe").length), "accent"),
    ],
  };
}

function reportLocataires(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.locataires
    .filter((locataire) => matchLocataire(locataire.id, f))
    .filter((locataire) => {
      if (!f.immeubleId && !f.bienId) return true;
      return ctx.contrats.some(
        (contrat) =>
          contrat.locataireId === locataire.id && matchImmeuble(contrat.immeubleId, f) && matchBien(contrat.bienId, f)
      );
    })
    .filter((locataire) => inDateRange(locataire.createdAt, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((locataire) => {
    const contrat = ctx.contrats.find((item) => item.locataireId === locataire.id);
    return {
      nom: locataireNomComplet(locataire),
      telephone: locataire.telephone || "—",
      email: locataire.email || "—",
      profession: locataire.profession || "—",
      piece: locataire.typePiece ? TYPE_PIECE_LABELS[locataire.typePiece] : "—",
      logement: contrat ? bienLabel(ctx, contrat.bienId) : "—",
      immeuble: contrat ? immeubleNom(ctx, contrat.immeubleId) : "—",
    };
  });

  return {
    title: "Rapport des locataires",
    description: "Répertoire des locataires et de leur logement.",
    columns: [
      { key: "nom", header: "Locataire" },
      { key: "telephone", header: "Téléphone" },
      { key: "email", header: "Email" },
      { key: "profession", header: "Profession" },
      { key: "piece", header: "Pièce" },
      { key: "logement", header: "Logement" },
      { key: "immeuble", header: "Immeuble" },
    ],
    rows,
    totals: [total("Locataires", String(filtered.length), "primary")],
  };
}

function reportContrats(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.contrats
    .filter((contrat) => matchImmeuble(contrat.immeubleId, f) && matchBien(contrat.bienId, f))
    .filter((contrat) => matchLocataire(contrat.locataireId, f))
    .filter((contrat) => !f.statut || contrat.statut === f.statut)
    .filter((contrat) => inDateRange(contrat.dateDebut, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((contrat) => ({
    reference: contratReference(contrat.id),
    immeuble: immeubleNom(ctx, contrat.immeubleId),
    bien: bienLabel(ctx, contrat.bienId),
    locataire: locataireNom(ctx, contrat.locataireId),
    debut: contrat.dateDebut ? formatDate(contrat.dateDebut) : "—",
    fin: contrat.dateFin ? formatDate(contrat.dateFin) : "—",
    loyer: formatFcfa(contrat.loyerMensuel),
    caution: formatFcfa(contrat.caution),
    statut: CONTRAT_STATUT_LABELS[contrat.statut],
  }));

  return {
    title: "Rapport des contrats",
    description: "Contrats de location avec loyer, caution et statut.",
    columns: [
      { key: "reference", header: "Référence" },
      { key: "immeuble", header: "Immeuble" },
      { key: "bien", header: "Bien" },
      { key: "locataire", header: "Locataire" },
      { key: "debut", header: "Début" },
      { key: "fin", header: "Fin" },
      { key: "loyer", header: "Loyer mensuel", align: "right" },
      { key: "caution", header: "Caution", align: "right" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Contrats", String(filtered.length), "primary"),
      total("Loyer mensuel cumulé", sumFcfa(filtered.map((contrat) => contrat.loyerMensuel)), "secondary"),
      total("Actifs", String(filtered.filter((contrat) => contrat.statut === "actif").length), "accent"),
    ],
  };
}

function reportLoyers(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.loyers
    .filter((loyer) => matchImmeuble(loyer.immeubleId, f) && matchBien(loyer.bienId, f) && matchLocataire(loyer.locataireId, f))
    .filter((loyer) => !f.statut || loyer.statut === f.statut)
    .filter((loyer) => inPeriodRange(loyer.periode, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((loyer) => ({
    periode: monthLabel(loyer.periode),
    locataire: locataireNom(ctx, loyer.locataireId),
    bien: bienLabel(ctx, loyer.bienId),
    montant: formatFcfa(loyer.montant),
    echeance: loyer.echeance ? formatDate(loyer.echeance) : "—",
    paye: formatFcfa(loyer.totalPaye),
    reste: formatFcfa(loyer.reste),
    statut: LOYER_STATUT_LABELS[loyer.statut],
  }));

  return {
    title: "Rapport des loyers",
    description: "Échéances de loyer avec montants dus, encaissés et reste à payer.",
    columns: [
      { key: "periode", header: "Période" },
      { key: "locataire", header: "Locataire" },
      { key: "bien", header: "Bien" },
      { key: "montant", header: "Montant", align: "right" },
      { key: "echeance", header: "Échéance" },
      { key: "paye", header: "Encaissé", align: "right" },
      { key: "reste", header: "Reste", align: "right" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Échéances", String(filtered.length), "primary"),
      total("Montant total", sumFcfa(filtered.map((loyer) => loyer.montant)), "secondary"),
      total("Encaissé", sumFcfa(filtered.map((loyer) => loyer.totalPaye)), "accent"),
      total("Reste à percevoir", sumFcfa(filtered.map((loyer) => loyer.reste)), "neutral"),
    ],
  };
}

function reportPaiements(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const contratOf = (paiement: Paiement) => ctx.contrats.find((item) => item.id === paiement.contratId);

  const filtered = ctx.paiements
    .filter((paiement) => {
      const contrat = contratOf(paiement);
      if (!contrat) return !f.immeubleId && !f.bienId && !f.locataireId;
      return matchImmeuble(contrat.immeubleId, f) && matchBien(contrat.bienId, f) && matchLocataire(contrat.locataireId, f);
    })
    .filter((paiement) => !f.statut || paiement.mode === f.statut)
    .filter((paiement) => inDateRange(paiement.date, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((paiement) => {
    const contrat = contratOf(paiement);
    return {
      date: paiement.date ? formatDate(paiement.date) : "—",
      locataire: contrat ? locataireNom(ctx, contrat.locataireId) : "—",
      periode: paiement.periode ? monthLabel(paiement.periode) : "—",
      montant: formatFcfa(paiement.montant),
      mode: MODE_PAIEMENT_LABELS[paiement.mode],
      reference: paiement.reference || paiementReference(paiement.id),
    };
  });

  return {
    title: "Rapport des paiements",
    description: "Encaissements par date, mode de paiement et locataire.",
    columns: [
      { key: "date", header: "Date" },
      { key: "locataire", header: "Locataire" },
      { key: "periode", header: "Période" },
      { key: "montant", header: "Montant", align: "right" },
      { key: "mode", header: "Mode de paiement" },
      { key: "reference", header: "Référence" },
    ],
    rows,
    totals: [
      total("Paiements", String(filtered.length), "primary"),
      total("Montant total", sumFcfa(filtered.map((paiement) => paiement.montant)), "secondary"),
    ],
  };
}

function reportImpayes(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.loyers
    .filter((loyer) => isImpaye(loyer.statut))
    .filter((loyer) => matchImmeuble(loyer.immeubleId, f) && matchBien(loyer.bienId, f) && matchLocataire(loyer.locataireId, f))
    .filter((loyer) => !f.statut || loyer.statut === f.statut)
    .filter((loyer) => inPeriodRange(loyer.periode, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((loyer) => ({
    locataire: locataireNom(ctx, loyer.locataireId),
    immeuble: immeubleNom(ctx, loyer.immeubleId),
    bien: bienLabel(ctx, loyer.bienId),
    periode: monthLabel(loyer.periode),
    echeance: loyer.echeance ? formatDate(loyer.echeance) : "—",
    montant: formatFcfa(loyer.montant),
    paye: formatFcfa(loyer.totalPaye),
    reste: formatFcfa(loyer.reste),
    retard: `${loyer.joursRetard} j`,
    statut: LOYER_STATUT_LABELS[loyer.statut],
  }));

  return {
    title: "Rapport des impayés",
    description: "Loyers en retard ou impayés et reste à recouvrer.",
    columns: [
      { key: "locataire", header: "Locataire" },
      { key: "immeuble", header: "Immeuble" },
      { key: "bien", header: "Bien" },
      { key: "periode", header: "Période" },
      { key: "echeance", header: "Échéance" },
      { key: "montant", header: "Montant", align: "right" },
      { key: "paye", header: "Encaissé", align: "right" },
      { key: "reste", header: "Reste dû", align: "right" },
      { key: "retard", header: "Retard", align: "right" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Échéances impayées", String(filtered.length), "primary"),
      total("Reste dû", sumFcfa(filtered.map((loyer) => loyer.reste)), "accent"),
      total("Dont impayés", String(filtered.filter((loyer) => loyer.statut === "impaye").length), "neutral"),
    ],
  };
}

function reportDepenses(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.depenses
    .filter((depense) => matchImmeuble(depense.immeubleId, f) && matchBien(depense.bienId, f))
    .filter((depense) => !f.statut || depense.categorie === f.statut)
    .filter((depense) => inDateRange(depense.date, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((depense) => ({
    date: depense.date ? formatDate(depense.date) : "—",
    categorie: DEPENSE_CATEGORIE_LABELS[depense.categorie],
    immeuble: immeubleNom(ctx, depense.immeubleId),
    bien: depense.bienId ? bienLabel(ctx, depense.bienId) : "—",
    montant: formatFcfa(depense.montant),
    description: depense.description || "—",
  }));

  return {
    title: "Rapport des dépenses",
    description: "Charges par catégorie, immeuble et bien.",
    columns: [
      { key: "date", header: "Date" },
      { key: "categorie", header: "Catégorie" },
      { key: "immeuble", header: "Immeuble" },
      { key: "bien", header: "Bien" },
      { key: "montant", header: "Montant", align: "right" },
      { key: "description", header: "Description" },
    ],
    rows,
    totals: [
      total("Dépenses", String(filtered.length), "primary"),
      total("Montant total", sumFcfa(filtered.map((depense) => depense.montant)), "accent"),
    ],
  };
}

function reportMaintenance(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.interventions
    .filter((intervention) => matchImmeuble(intervention.immeubleId, f) && matchBien(intervention.bienId, f))
    .filter((intervention) => !f.statut || intervention.statut === f.statut)
    .filter((intervention) => inDateRange(intervention.date, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((intervention) => ({
    date: intervention.date ? formatDate(intervention.date) : "—",
    probleme: intervention.probleme || "—",
    immeuble: immeubleNom(ctx, intervention.immeubleId),
    bien: intervention.bienId ? bienLabel(ctx, intervention.bienId) : "—",
    priorite: INTERVENTION_PRIORITE_LABELS[intervention.priorite],
    statut: INTERVENTION_STATUT_LABELS[intervention.statut],
    prestataire: intervention.prestataire || "—",
    cout: formatFcfa(intervention.cout),
  }));

  return {
    title: "Rapport de maintenance",
    description: "Interventions par priorité, statut, prestataire et coût.",
    columns: [
      { key: "date", header: "Date" },
      { key: "probleme", header: "Problème" },
      { key: "immeuble", header: "Immeuble" },
      { key: "bien", header: "Bien" },
      { key: "priorite", header: "Priorité" },
      { key: "statut", header: "Statut" },
      { key: "prestataire", header: "Prestataire" },
      { key: "cout", header: "Coût", align: "right" },
    ],
    rows,
    totals: [
      total("Interventions", String(filtered.length), "primary"),
      total("Coût total", sumFcfa(filtered.map((intervention) => intervention.cout)), "accent"),
      total("Terminées", String(filtered.filter((intervention) => intervention.statut === "termine").length), "secondary"),
    ],
  };
}

function reportDocuments(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.documents
    .filter((document) => matchImmeuble(document.immeubleId, f) && matchBien(document.bienId, f))
    .filter((document) => !f.statut || document.type === f.statut)
    .filter((document) => inDateRange(document.date, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((document) => ({
    date: document.date ? formatDate(document.date) : "—",
    nom: document.nom || "—",
    type: DOCUMENT_TYPE_LABELS[document.type],
    immeuble: immeubleNom(ctx, document.immeubleId),
    bien: document.bienId ? bienLabel(ctx, document.bienId) : "—",
    fichier: document.fichierNom || "—",
    taille: formatFileSize(document.fichierTaille),
  }));

  return {
    title: "Rapport des documents",
    description: "Bibliothèque de documents par type et rattachement.",
    columns: [
      { key: "date", header: "Date" },
      { key: "nom", header: "Document" },
      { key: "type", header: "Type" },
      { key: "immeuble", header: "Immeuble" },
      { key: "bien", header: "Bien" },
      { key: "fichier", header: "Fichier" },
      { key: "taille", header: "Taille", align: "right" },
    ],
    rows,
    totals: [
      total("Documents", String(filtered.length), "primary"),
      total("Taille totale", formatFileSize(filtered.reduce((sum, document) => sum + (document.fichierTaille || 0), 0)), "secondary"),
    ],
  };
}

function reportRevenus(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const loyers = ctx.loyers
    .filter((loyer) => matchImmeuble(loyer.immeubleId, f) && matchBien(loyer.bienId, f) && matchLocataire(loyer.locataireId, f))
    .filter((loyer) => inPeriodRange(loyer.periode, f.dateFrom, f.dateTo));
  const depenses = ctx.depenses
    .filter((depense) => matchImmeuble(depense.immeubleId, f) && matchBien(depense.bienId, f))
    .filter((depense) => inPeriodRange(periodKeyOf(depense.date), f.dateFrom, f.dateTo));

  const periodes = Array.from(
    new Set([...loyers.map((loyer) => loyer.periode), ...depenses.map((depense) => periodKeyOf(depense.date))])
  )
    .filter(Boolean)
    .sort((a, b) => (a < b ? 1 : -1));

  const rows = periodes.map<ReportRow>((periode) => {
    const loyersPeriode = loyers.filter((loyer) => loyer.periode === periode);
    const depensesPeriode = depenses.filter((depense) => periodKeyOf(depense.date) === periode);
    const attendu = loyersPeriode.reduce((sum, loyer) => sum + loyer.montant, 0);
    const encaisse = loyersPeriode.reduce((sum, loyer) => sum + loyer.totalPaye, 0);
    const impaye = loyersPeriode.reduce((sum, loyer) => sum + loyer.reste, 0);
    const depense = depensesPeriode.reduce((sum, item) => sum + item.montant, 0);
    return {
      periode: monthLabel(periode),
      attendu: formatFcfa(attendu),
      encaisse: formatFcfa(encaisse),
      impaye: formatFcfa(impaye),
      depense: formatFcfa(depense),
      resultat: formatFcfa(encaisse - depense),
    };
  });

  const totalEncaisse = loyers.reduce((sum, loyer) => sum + loyer.totalPaye, 0);
  const totalDepense = depenses.reduce((sum, depense) => sum + depense.montant, 0);

  return {
    title: "Rapport des revenus",
    description: "Loyers attendus, encaissés, impayés, dépenses et résultat net par mois.",
    columns: [
      { key: "periode", header: "Période" },
      { key: "attendu", header: "Loyers attendus", align: "right" },
      { key: "encaisse", header: "Encaissé", align: "right" },
      { key: "impaye", header: "Impayés", align: "right" },
      { key: "depense", header: "Dépenses", align: "right" },
      { key: "resultat", header: "Résultat net", align: "right" },
    ],
    rows,
    totals: [
      total("Loyers attendus", sumFcfa(loyers.map((loyer) => loyer.montant)), "primary"),
      total("Encaissé", formatFcfa(totalEncaisse), "secondary"),
      total("Impayés", sumFcfa(loyers.map((loyer) => loyer.reste)), "accent"),
      total("Résultat net", formatFcfa(totalEncaisse - totalDepense), "neutral"),
    ],
    note: "Le résultat net correspond aux encaissements moins les dépenses de la période.",
  };
}

function reportGlobal(ctx: OwnerReportContext, f: ReportFilters): ReportResult {
  const loyers = ctx.loyers
    .filter((loyer) => matchImmeuble(loyer.immeubleId, f) && matchBien(loyer.bienId, f) && matchLocataire(loyer.locataireId, f))
    .filter((loyer) => inPeriodRange(loyer.periode, f.dateFrom, f.dateTo));
  const paiements = ctx.paiements
    .filter((paiement) => {
      const contrat = ctx.contrats.find((item) => item.id === paiement.contratId);
      if (!contrat) return !f.immeubleId && !f.bienId && !f.locataireId;
      return matchImmeuble(contrat.immeubleId, f) && matchBien(contrat.bienId, f) && matchLocataire(contrat.locataireId, f);
    })
    .filter((paiement) => inDateRange(paiement.date, f.dateFrom, f.dateTo));
  const depenses = ctx.depenses
    .filter((depense) => matchImmeuble(depense.immeubleId, f) && matchBien(depense.bienId, f))
    .filter((depense) => inDateRange(depense.date, f.dateFrom, f.dateTo));
  const interventions = ctx.interventions
    .filter((intervention) => matchImmeuble(intervention.immeubleId, f) && matchBien(intervention.bienId, f))
    .filter((intervention) => inDateRange(intervention.date, f.dateFrom, f.dateTo));
  const documents = ctx.documents
    .filter((document) => matchImmeuble(document.immeubleId, f) && matchBien(document.bienId, f))
    .filter((document) => inDateRange(document.date, f.dateFrom, f.dateTo));
  const biens = ctx.biens.filter((bien) => matchImmeuble(bien.immeubleId, f) && matchBien(bien.id, f));
  const contrats = ctx.contrats.filter(
    (contrat) => matchImmeuble(contrat.immeubleId, f) && matchBien(contrat.bienId, f) && matchLocataire(contrat.locataireId, f)
  );

  const encaisse = loyers.reduce((sum, loyer) => sum + loyer.totalPaye, 0);
  const depense = depenses.reduce((sum, item) => sum + item.montant, 0);

  const rows: ReportRow[] = [
    { indicateur: "Immeubles", valeur: String(ctx.immeubles.filter((item) => matchImmeuble(item.id, f)).length) },
    { indicateur: "Biens", valeur: String(biens.length) },
    { indicateur: "Biens occupés", valeur: String(biens.filter((bien) => bien.statut === "occupe").length) },
    { indicateur: "Locataires", valeur: String(ctx.locataires.filter((locataire) => matchLocataire(locataire.id, f)).length) },
    { indicateur: "Contrats", valeur: String(contrats.length) },
    { indicateur: "Contrats actifs", valeur: String(contrats.filter((contrat) => contrat.statut === "actif").length) },
    { indicateur: "Échéances de loyer", valeur: String(loyers.length) },
    { indicateur: "Loyers attendus", valeur: sumFcfa(loyers.map((loyer) => loyer.montant)) },
    { indicateur: "Encaissements", valeur: formatFcfa(encaisse) },
    { indicateur: "Impayés", valeur: sumFcfa(loyers.map((loyer) => loyer.reste)) },
    { indicateur: "Dépenses", valeur: formatFcfa(depense) },
    { indicateur: "Résultat net", valeur: formatFcfa(encaisse - depense) },
    { indicateur: "Interventions de maintenance", valeur: String(interventions.length) },
    { indicateur: "Coût de maintenance", valeur: sumFcfa(interventions.map((intervention) => intervention.cout)) },
    { indicateur: "Documents", valeur: String(documents.length) },
    { indicateur: "Paiements enregistrés", valeur: String(paiements.length) },
  ];

  return {
    title: "Rapport global de l'activité",
    description: "Synthèse complète de votre patrimoine et de votre activité sur la période.",
    columns: [
      { key: "indicateur", header: "Indicateur" },
      { key: "valeur", header: "Valeur", align: "right" },
    ],
    rows,
    totals: [
      total("Encaissements", formatFcfa(encaisse), "primary"),
      total("Impayés", sumFcfa(loyers.map((loyer) => loyer.reste)), "accent"),
      total("Dépenses", formatFcfa(depense), "neutral"),
      total("Résultat net", formatFcfa(encaisse - depense), "secondary"),
    ],
  };
}

const BUILDERS: Record<string, (ctx: OwnerReportContext, f: ReportFilters) => ReportResult> = {
  immeubles: reportImmeubles,
  biens: reportBiens,
  locataires: reportLocataires,
  contrats: reportContrats,
  loyers: reportLoyers,
  paiements: reportPaiements,
  impayes: reportImpayes,
  depenses: reportDepenses,
  maintenance: reportMaintenance,
  documents: reportDocuments,
  revenus: reportRevenus,
  global: reportGlobal,
};

export function buildOwnerReport(typeId: string, filters: ReportFilters, ctx: OwnerReportContext): ReportResult {
  const builder = BUILDERS[typeId] ?? reportGlobal;
  return builder(ctx, filters);
}